import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import axios from "axios";
import { Camera, UserCircle2 } from "lucide-react";
import { getAdminDetails } from "../../redux/actions/campaignActions";
import Loader from "../../components/Loader/Loader";
import PageShell from "../../components/layout/PageShell";
import PageHead from "../../components/layout/PageHead";
import Field from "../../components/v3/Field";
import Btn from "../../components/v3/Btn";
import Alert from "../../components/v3/Alert";

const New = () => {
  const [file, setFile] = useState({ files: "", preview: "" });
  const [imageError, setImageError] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const adminView = useSelector((state) => state.adminView);
  const { adminDetails } = adminView;

  const token = localStorage.getItem("accessToken");
  const id = JSON.parse(localStorage.getItem("admin")).data.id;

  useEffect(() => {
    if (adminView?.loading !== undefined) setLoading(adminView.loading);
  }, [adminView]);

  useEffect(() => {
    dispatch(getAdminDetails(token, id));
  }, [dispatch]);

  useEffect(() => {
    if (adminDetails?.data?.profilePic) {
      setFile({ files: "", preview: adminDetails.data.profilePic });
    } else {
      setFile({ files: "", preview: "" });
    }
  }, [adminDetails]);

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "all" });

  useEffect(() => {
    if (adminDetails?.data) {
      setValue("firstname", adminDetails.data.firstname);
      setValue("lastname", adminDetails.data.lastname);
      setValue("email", adminDetails.data.email);
      setValue("phone", adminDetails.data.mobile);
      setValue("marital_status", adminDetails.data.marital_status);
      setValue("gender", adminDetails.data.gender);
    }
  }, [adminDetails, setValue]);

  const profileImage = async (e) => {
    const baseUrl = process.env.REACT_APP_API + "update-userprofilepic";
    const selected = e.target.files[0];
    const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
    if (!selected) return;
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setImageError("Please upload a JPG, JPEG, or PNG image");
      return;
    }
    setImageError("");
    const formData = new FormData();
    setLoading(true);
    formData.append("user_id", id);
    formData.append("file", selected);
    try {
      const response = await axios.patch(baseUrl, formData, {
        headers: { "Content-Type": "multipart/form-data", "x-access-token": token },
      });
      setFile({
        files: response?.data?.data?.document_name,
        preview: response?.data?.data?.document_name,
      });
    } catch (err) {
      setImageError(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k === "preview") return;
      formData.append(k, v);
    });
    formData.append("id", id);
    // Intentionally does NOT stamp profile_context='INVESTOR_PROFILE' here: this is
    // the logged-in admin's own 6-field /profile edit, not an investor-profile
    // completion surface, so it must not stamp full investor-profile completion.
    // That completion (which clears the PENDING invest-gate) is stamped only by
    // the portal's investor Profile form.
    if (file.files !== "" && adminDetails?.data?.profilePic === "") {
      formData.append("profile_pic", file.files);
    } else if (file.files !== "" && adminDetails?.data?.profilePic) {
      formData.append("profile_pic", file.files);
      formData.append("profile_pic_name", adminDetails.data.profilePic);
    }
    try {
      const res = await axios({
        method: "PATCH",
        url: process.env.REACT_APP_API + "admin/update-user-details",
        data: formData,
        headers: { "Content-Type": "multipart/form-data", "x-access-token": token },
      });
      if (res.data.status === 200) {
        Swal.fire({
          title: "Success",
          timer: 2500,
          timerProgressBar: true,
          text: res.data.message,
          icon: "success",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        timer: 2500,
        timerProgressBar: true,
        text: err?.response?.data?.message || "Save failed",
        icon: "error",
        confirmButtonColor: "#f27474",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Edit profile" />

      <section className="card">
        <div className="card__body" style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <label
              style={{
                position: "relative",
                width: 120, height: 120,
                borderRadius: "50%",
                overflow: "hidden",
                cursor: "pointer",
                border: "1px solid var(--bd)",
                background: "var(--bg-card-2)",
                display: "block",
              }}
            >
              {file.preview ? (
                <img
                  src={file.preview}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    color: "var(--tx-3)",
                  }}
                >
                  <UserCircle2 size={64} strokeWidth={1.25} />
                </span>
              )}
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  right: 6, bottom: 6,
                  width: 28, height: 28,
                  borderRadius: "50%",
                  background: "var(--ink-700)",
                  color: "var(--n-0)",
                  display: "grid", placeItems: "center",
                  border: "2px solid var(--bg-card)",
                }}
              >
                <Camera size={14} />
              </span>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={profileImage}
                style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
              />
            </label>
            {imageError && <Alert tone="danger">{imageError}</Alert>}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, minWidth: 320 }}>
            <div className="form-grid form-grid--2col">
              <Field
                label="First name"
                name="firstname"
                error={errors.firstname?.message}
                {...register("firstname", {
                  required: "First name is required",
                  pattern: {
                    value: /^[\p{L}\p{M}._' -]*$/u,
                    message: "Letters only",
                  },
                })}
              />
              <Field
                label="Last name"
                name="lastname"
                error={errors.lastname?.message}
                {...register("lastname", {
                  required: "Last name is required",
                  pattern: {
                    value: /^[\p{L}\p{M}_' -]*$/u,
                    message: "Letters only",
                  },
                })}
              />
              <Field
                label="Email"
                name="email"
                type="email"
                error={errors.email?.message}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
              <Field
                label="Phone"
                name="phone"
                error={errors.phone?.message}
                {...register("phone", { required: "Phone is required" })}
              />
              <Field
                as="select"
                label="Marital status"
                name="marital_status"
                error={errors.marital_status?.message}
                {...register("marital_status", { required: "Required" })}
              >
                <option value="">Select…</option>
                <option value="Married">Married</option>
                <option value="Single">Single</option>
                <option value="Divorce">Divorce</option>
              </Field>
              <Field
                as="select"
                label="Gender"
                name="gender"
                error={errors.gender?.message}
                {...register("gender", { required: "Required" })}
              >
                <option value="">Select…</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </Field>
            </div>

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="primary" type="submit">Save</Btn>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
};

export default New;
